import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useToast } from "@/app/providers/ToastProvider";
import {
  deleteSubscribe,
  listSubscribes,
  searchSubscribe,
  subscribeStateLabel,
  updateSubscribeStatus,
  type Subscribe,
} from "@/features/subscribe/api";
import { mediaIdOf } from "@/shared/lib/format";
import { proxyImageUrl } from "@/shared/lib/image";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";

/** 订阅列表：启停 / 搜索 / 删除 */
export function SubscribeListPage() {
  const { toast } = useToast();
  const [items, setItems] = useState<Subscribe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [filter, setFilter] = useState<"all" | "R" | "P" | "S">("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSubscribes();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((s) => s.state === filter);
  }, [items, filter]);

  async function withBusy(id: number, fn: () => Promise<void>) {
    setBusyId(id);
    try {
      await fn();
    } catch (e) {
      toast(e instanceof Error ? e.message : "操作失败", "error");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="订阅"
        description={`${items.length} 条 · 筛选后 ${visible.length} 条`}
        actions={
          <>
            <Button variant="ghost" onClick={() => void load()} disabled={loading}>
              刷新
            </Button>
            <Link to="/search">
              <Button>去搜索添加</Button>
            </Link>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            ["all", "全部"],
            ["R", "订阅中"],
            ["P", "已暂停"],
            ["S", "已完成"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              "h-8 rounded-[var(--radius-sm)] border px-3 text-[13px] transition-colors",
              filter === key
                ? "border-brand/40 bg-brand-muted text-brand"
                : "border-border text-fg-muted hover:bg-bg-hover",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 加载中…
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error}
        </div>
      ) : null}

      {!loading && !error && visible.length === 0 ? (
        <EmptyState
          title="暂无订阅"
          description="从搜索页找到影片后一键订阅"
        />
      ) : null}

      <ul className="flex flex-col gap-2">
        {visible.map((sub) => {
          const id = sub.id;
          if (id == null) return null;
          const busy = busyId === id;
          const mid = mediaIdOf({
            tmdbid: sub.tmdbid,
            doubanid: sub.doubanid,
            bangumiid: sub.bangumiid,
          });
          return (
            <li
              key={id}
              className="flex gap-3 rounded-[var(--radius-md)] border border-border bg-bg-card p-3"
            >
              <div className="h-[88px] w-[60px] shrink-0 overflow-hidden rounded-[var(--radius-sm)] bg-bg-hover">
                {proxyImageUrl(sub.poster) ? (
                  <img
                    src={proxyImageUrl(sub.poster)}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <h3 className="truncate text-[14px] font-medium text-fg">
                    {sub.name || "未命名"}
                  </h3>
                  <span className="text-[12px] text-fg-subtle">
                    {[sub.year, sub.type, sub.season != null ? `S${sub.season}` : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-[12px] text-fg-muted">
                  <span
                    className={cn(
                      "rounded px-1.5 py-0.5",
                      sub.state === "R" && "bg-brand-muted text-brand",
                      sub.state === "P" && "bg-bg-hover text-fg-muted",
                      sub.state === "S" && "bg-success/15 text-success",
                    )}
                  >
                    {subscribeStateLabel(sub.state)}
                  </span>
                  {sub.type === "电视剧" || sub.total_episode ? (
                    <span>
                      进度{" "}
                      {sub.completed_episode ??
                        Math.max(
                          (sub.total_episode || 0) - (sub.lack_episode || 0),
                          0,
                        )}
                      /{sub.total_episode || "?"}
                      {sub.lack_episode ? ` · 缺 ${sub.lack_episode}` : ""}
                    </span>
                  ) : null}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {sub.state === "R" ? (
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() =>
                        void withBusy(id, async () => {
                          await updateSubscribeStatus(id, "P");
                          toast("已暂停", "success");
                          await load();
                        })
                      }
                    >
                      暂停
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() =>
                        void withBusy(id, async () => {
                          await updateSubscribeStatus(id, "R");
                          toast("已恢复订阅", "success");
                          await load();
                        })
                      }
                    >
                      恢复
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                      void withBusy(id, async () => {
                        await searchSubscribe(id);
                        toast("已触发订阅搜索", "success");
                      })
                    }
                  >
                    立即搜索
                  </Button>
                  {mid ? (
                    <Link to={`/search?media=${encodeURIComponent(mid)}`}>
                      <Button variant="ghost">搜资源</Button>
                    </Link>
                  ) : null}
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm(`确定删除订阅「${sub.name}」？`)) return;
                      void withBusy(id, async () => {
                        await deleteSubscribe(id);
                        toast("已删除", "success");
                        await load();
                      });
                    }}
                  >
                    删除
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
